import React, { useEffect, useMemo, useState, useCallback, Suspense } from 'react';
import { purgeExpiredLocalState } from './hooks/useLocalState';
import ErrorBoundary from './components/ErrorBoundary';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { getTheme } from './theme';
import { ColorModeContext, COLOR_MODE_STORAGE_KEY } from './context/ColorModeContext';
import { AuthProvider } from './context/AuthContext';
import { ScrollProvider } from './context/ScrollContext';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ProtectedRoute from './components/ProtectedRoute';
// ★ 2026-06-11: PointConsumptionStation 復元 (= V3 動的請求の課金 toast、 window 'point-consumed' を listen)
import PointConsumptionStation from './components/common/PointConsumptionStation';
// ★ 2026-06-19: 公開告知 modal (= 初回1回だけ中央表示)
import LaunchAnnounceModal from './components/modals/LaunchAnnounceModal';

// ★ 軽量化 2026-05-13 iter 4: LCP-critical の根本見直し
//   旧: LandingPage / LoginPage / AipenLoginPage / RegisterPage を全部 eager (= 4 ページ分が main 同梱)
//   新: LandingPage **のみ** eager (= 未認証の最初の到達点、 真の LCP-critical)
//       LoginPage / AipenLoginPage / RegisterPage は「ボタンクリック後の遷移」 なので lazy で OK
//       (= 初回画面は LandingPage、 そこから「ログイン」 「新規登録」 ボタンで遷移する流れ)
import LandingPage from './pages/LandingPage';

// それ以外のページは React.lazy で code-split
// 旧: 全ページ eager → 初回 bundle に LandingPage 訪問だけのユーザーも全画面コードを読み込む
// 新: ルート単位で動的 import、各ページは初回アクセス時のみ取得
const LoginPage            = React.lazy(() => import('./pages/LoginPage'));
const AipenLoginPage       = React.lazy(() => import('./pages/AipenLoginPage'));
const RegisterPage         = React.lazy(() => import('./pages/RegisterPage'));
// ★ 章立てボード Canvas 再設計 検証ラボ(公開・合成データ、docs/board-rebuild-design.md)
const BoardLab             = React.lazy(() => import('./pages/BoardLab'));
const LinkCallbackPage     = React.lazy(() => import('./pages/LinkCallbackPage'));
const HandoffPage          = React.lazy(() => import('./pages/HandoffPage'));
const ForgotPasswordPage   = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage    = React.lazy(() => import('./pages/ResetPasswordPage'));
const PurchasePage         = React.lazy(() => import('./pages/PurchasePage'));
const PointHistoryPage     = React.lazy(() => import('./pages/PointHistoryPage'));
const DashboardPage        = React.lazy(() => import('./pages/DashboardPage'));
// ★ 2026-06-14: CreatorPage(/creator/:id) は廃止 → 公開応援ページ /gift/:id へ恒久リダイレクト。
//   GiftPage(/gift/:creatorId) が応援の公開ページとして完全に置き換え済み(全ての共有/設定/aipen導線が /gift/)。
//   CreatorPage 固有だった「作品リンク」は 2026-06-13 に廃止済み、名前/bio/応援実績は GiftPageView が表示する。
//   route は残置し旧リンク(直貼り/funnel内の戻る)を /gift/ へ救済。backend GET /api/creators/:id は GiftPage と共用なので温存。
const CreatorRedirect = () => {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/gift/${id}${search || ''}`} replace />;
};
const ProjectsPage         = React.lazy(() => import('./pages/ProjectsPage'));
const ProjectDetailRouter  = React.lazy(() => import('./pages/ProjectDetailRouter'));
const ProjectActivityPage  = React.lazy(() => import('./pages/ProjectActivityPage'));
// 監査 2026-05-12 Phase γ-1: 共同編集招待受諾画面
const InviteAcceptPage     = React.lazy(() => import('./pages/InviteAcceptPage'));
const TermsPage            = React.lazy(() => import('./pages/TermsPage'));
const GuidePage            = React.lazy(() => import('./pages/GuidePage'));
const GuideArticlePage     = React.lazy(() => import('./pages/GuideArticlePage'));
const AdminGuidePage       = React.lazy(() => import('./pages/AdminGuidePage'));
const PrivacyPage          = React.lazy(() => import('./pages/PrivacyPage'));
const LegalPage            = React.lazy(() => import('./pages/LegalPage'));
const StyleProfilePage     = React.lazy(() => import('./pages/StyleProfilePage'));
const PersonasPage         = React.lazy(() => import('./pages/PersonasPage'));
const FullscreenPanelPage  = React.lazy(() => import('./pages/FullscreenPanelPage'));
const NotFoundPage         = React.lazy(() => import('./pages/NotFoundPage'));
const WalletPage           = React.lazy(() => import('./pages/WalletPage'));
const PurchaseCompletePage = React.lazy(() => import('./pages/PurchaseCompletePage'));
const GiftPage             = React.lazy(() => import('./pages/GiftPage'));
const GiftCompletePage     = React.lazy(() => import('./pages/GiftCompletePage'));
const SubscriptionPage     = React.lazy(() => import('./pages/SubscriptionPage'));
const WithdrawalPage       = React.lazy(() => import('./pages/WithdrawalPage'));
const OfferWallPage        = React.lazy(() => import('./pages/OfferWallPage'));
const AdminKycPage         = React.lazy(() => import('./pages/AdminKycPage'));
const AdminWithdrawalsPage = React.lazy(() => import('./pages/AdminWithdrawalsPage'));
const AdminFeaturesPage    = React.lazy(() => import('./pages/AdminFeaturesPage'));
const AdminExtractionQueuePage = React.lazy(() => import('./pages/AdminExtractionQueuePage'));
// ★ 2026-06-01: 全 append-only 台帳 (PointTransaction / Gift / PaymentOrder / Orphan) を 横断 閲覧
const AdminLedgersPage     = React.lazy(() => import('./pages/AdminLedgersPage'));

// ★ WithHeader: Header + Footer を自動付与するラッパー (= 2026-05-08)
//   ページ本文を min-height: calc(100vh - footer 分) で挟むと、 短いページでも
//   Footer が画面下部に張り付き、 長いページでは自然に末尾に配置される。
const WithHeader = ({ children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
    <Header />
    <Box sx={{ flex: 1 }}>{children}</Box>
    <Footer />
  </Box>
);
const Protected = ({ children }) => <WithHeader><ProtectedRoute>{children}</ProtectedRoute></WithHeader>;
// 執筆エディタページ用: PC / モバイル両方でサイトヘッダーを完全に非表示にし、画面を最大限使う
//   (旧 ProtectedNoHeaderMobile は PC で Header を出していたが、サイドバー + エディタで縦が
//    詰まるので Header は邪魔。プロジェクト一覧へ戻るリンクはページ内左サイドバーに既存)
const ProtectedNoHeader = ({ children }) => (
  <ProtectedRoute>{children}</ProtectedRoute>
);

// Suspense フォールバック — チャンク読み込み中の最小表示
// シンプルなスピナーのみ (skeleton はページごとに大きく違うため共通化困難)
const PageFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
    }}
  >
    <CircularProgress size={32} />
  </Box>
);

function App() {
  // 起動時に期限切れの localStorage エントリを掃除 (フォーム自動保存系)
  useEffect(() => {
    try { purgeExpiredLocalState(30); } catch {}
  }, []);

  // ★ ライト/ダーク モード状態 (= localStorage に永続化)
  const [mode, setMode] = useState(() => {
    try {
      const saved = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
      return saved === 'dark' || saved === 'light' ? saved : 'light';
    } catch { return 'light'; }
  });

  const toggleColorMode = useCallback(() => {
    setMode(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      try { localStorage.setItem(COLOR_MODE_STORAGE_KEY, next); } catch {}
      return next;
    });
  }, []);

  const setModeStable = useCallback((next) => {
    if (next !== 'light' && next !== 'dark') return;
    setMode(next);
    try { localStorage.setItem(COLOR_MODE_STORAGE_KEY, next); } catch {}
  }, []);

  const colorModeValue = useMemo(() => ({
    mode, toggleColorMode, setMode: setModeStable,
  }), [mode, toggleColorMode, setModeStable]);

  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <ErrorBoundary>
    <ColorModeContext.Provider value={colorModeValue}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ScrollProvider>
        <Router>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              {/* 公開ページ */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<WithHeader><LoginPage /></WithHeader>} />
              <Route path="/login/aipen" element={<WithHeader><AipenLoginPage /></WithHeader>} />
              <Route path="/register" element={<WithHeader><RegisterPage /></WithHeader>} />
              <Route path="/auth/link-callback" element={<WithHeader><LinkCallbackPage /></WithHeader>} />
              {/* Phase 3: aipen handoff 着地ページ (ヘッダー無し) */}
              <Route path="/handoff" element={<HandoffPage />} />
              {/* パスワードリセット */}
              <Route path="/forgot-password" element={<WithHeader><ForgotPasswordPage /></WithHeader>} />
              <Route path="/reset-password/:token" element={<WithHeader><ResetPasswordPage /></WithHeader>} />
              <Route path="/analyze" element={<Protected><ProjectsPage /></Protected>} />
              <Route path="/purchase" element={<PurchasePage />} />
              <Route path="/purchase/complete" element={<PurchaseCompletePage />} />
              {/* ★ 応援ページは公開 (= SNS シェア対応): 未ログインでも閲覧可、 送信時のみ login 要求 */}
              <Route path="/gift/:creatorId" element={<WithHeader><GiftPage /></WithHeader>} />
              <Route path="/gift/:creatorId/complete" element={<Protected><GiftCompletePage /></Protected>} />
              <Route path="/points/history" element={<WithHeader><PointHistoryPage /></WithHeader>} />
              {/* ★ 廃止: 旧クリエイター公開ページ → 公開応援ページ /gift/:id へリダイレクト */}
              <Route path="/creator/:id" element={<CreatorRedirect />} />
              <Route path="/board-lab" element={<BoardLab />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/legal" element={<LegalPage />} />
              {/* 使い方ガイド/ヘルプ(公開・note入門マガジン由来) */}
              <Route path="/guide" element={<WithHeader><GuidePage /></WithHeader>} />
              <Route path="/guide/:slug" element={<WithHeader><GuideArticlePage /></WithHeader>} />

              {/* 認証必須ページ */}
              <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
              <Route path="/projects" element={<Protected><ProjectsPage /></Protected>} />
              {/* 監査 2026-05-12 Phase γ-1: 共同編集招待 (= 認証なしでも閲覧可、 承諾は要ログイン) */}
              <Route path="/invite/:token" element={<InviteAcceptPage />} />
              <Route path="/projects/:id" element={<ProtectedNoHeader><ProjectDetailRouter /></ProtectedNoHeader>} />
              <Route path="/projects/:id/activity" element={<Protected><ProjectActivityPage /></Protected>} />
              <Route path="/personas" element={<Protected><PersonasPage /></Protected>} />
              <Route path="/fullscreen/:panelType/:projectId" element={<Protected><FullscreenPanelPage /></Protected>} />
              <Route path="/wallet" element={<Protected><WalletPage /></Protected>} />
              <Route path="/offerwall" element={<Protected><OfferWallPage /></Protected>} />
              <Route path="/subscription" element={<Protected><SubscriptionPage /></Protected>} />
              <Route path="/withdrawal" element={<Protected><WithdrawalPage /></Protected>} />
              <Route path="/profile/style-dna" element={<Protected><StyleProfilePage /></Protected>} />

              {/* 運営管理 (= 真のガードは backend の ADMIN_USER_IDS 判定) */}
              <Route path="/admin/kyc" element={<Protected><AdminKycPage /></Protected>} />
              <Route path="/admin/withdrawals" element={<Protected><AdminWithdrawalsPage /></Protected>} />
              <Route path="/admin/features" element={<Protected><AdminFeaturesPage /></Protected>} />
              <Route path="/admin/extraction-queue" element={<Protected><AdminExtractionQueuePage /></Protected>} />
              <Route path="/admin/ledgers" element={<Protected><AdminLedgersPage /></Protected>} />
              <Route path="/admin/guide" element={<Protected><AdminGuidePage /></Protected>} />

              {/* 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          {/* ★ 2026-06-11: 動的請求の課金 toast (全ページ共通、 window 'point-consumed' を listen) */}
          <PointConsumptionStation />
          {/* ★ 2026-06-19: 公開告知 modal (= 初回1回だけ中央表示、 localStorage gated) */}
          <LaunchAnnounceModal />
        </Router>
        </ScrollProvider>
      </AuthProvider>
    </ThemeProvider>
    </ColorModeContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
